// src/components/ecommerce/ContactModal.jsx
import React, { useState } from 'react';
import { 
  X,
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  MessageCircle,
  User,
  Building,
  Globe,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

export const ContactModal = ({ isOpen, onClose, storeData }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    inquiryType: 'general'
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const inquiryTypes = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'product', label: 'Product Question' },
    { value: 'order', label: 'Order Support' },
    { value: 'shipping', label: 'Shipping & Delivery' },
    { value: 'returns', label: 'Returns & Exchanges' },
    { value: 'technical', label: 'Technical Support' },
    { value: 'partnership', label: 'Business Partnership' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setSending(true);
    setError('');

    try {
      // Here you would integrate with your contact form service
      // For now, we'll simulate sending
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Save to localStorage for demo purposes
      const contacts = JSON.parse(localStorage.getItem('ecommerce_contacts') || '[]');
      contacts.push({
        ...formData,
        id: Date.now(),
        timestamp: new Date().toISOString(),
        storeId: storeData?.id
      });
      localStorage.setItem('ecommerce_contacts', JSON.stringify(contacts));
      
      setSent(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setSent(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          inquiryType: 'general'
        });
      }, 3000);
      
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleDirectContact = (method) => {
    switch (method) {
      case 'phone':
        if (storeData?.phone) {
          window.open(`tel:${storeData.phone}`);
        }
        break;
      case 'email':
        if (storeData?.email) {
          window.open(`mailto:${storeData.email}`);
        }
        break;
      case 'whatsapp':
        if (storeData?.whatsappNumber) {
          const message = encodeURIComponent(`Hi! I'm interested in your products from ${storeData.storeName || 'your store'}.`);
          window.open(`https://wa.me/${storeData.whatsappNumber}?text=${message}`);
        }
        break;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Contact Us</h2>
            <p className="text-gray-600">
              Get in touch with {storeData?.storeName || 'our team'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Contact Information */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                
                <div className="space-y-4">
                  {storeData?.phone && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Phone className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Phone</p>
                        <button
                          onClick={() => handleDirectContact('phone')}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          {storeData.phone}
                        </button>
                      </div>
                    </div>
                  )}

                  {storeData?.email && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Mail className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Email</p>
                        <button
                          onClick={() => handleDirectContact('email')}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          {storeData.email}
                        </button>
                      </div>
                    </div>
                  )}

                  {storeData?.address && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <MapPin className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Address</p>
                        <p className="text-gray-600">{storeData.address}</p>
                      </div>
                    </div>
                  )}

                  {storeData?.businessHours && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <Clock className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Business Hours</p>
                        <p className="text-gray-600">{storeData.businessHours}</p>
                      </div>
                    </div>
                  )}

                  {storeData?.website && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <Globe className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Website</p>
                        <a
                          href={storeData.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          {storeData.website}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Contact Options */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Quick Contact</h4>
                <div className="grid grid-cols-1 gap-3">
                  {storeData?.phone && (
                    <button
                      onClick={() => handleDirectContact('phone')}
                      className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors"
                    >
                      <Phone className="h-5 w-5 text-green-600" />
                      <span className="font-medium text-gray-900">Call Now</span>
                    </button>
                  )}

                  {storeData?.whatsappNumber && (
                    <button
                      onClick={() => handleDirectContact('whatsapp')}
                      className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors"
                    >
                      <MessageCircle className="h-5 w-5 text-green-600" />
                      <span className="font-medium text-gray-900">WhatsApp</span>
                    </button>
                  )}

                  {storeData?.email && (
                    <button
                      onClick={() => handleDirectContact('email')}
                      className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <Mail className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-gray-900">Send Email</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Send us a Message</h3>
              
              {sent ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Message Sent!</h4>
                  <p className="text-gray-600">
                    Thank you for contacting us. We'll get back to you soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Your full name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="your.email@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Your phone number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Inquiry Type
                      </label>
                      <select
                        value={formData.inquiryType}
                        onChange={(e) => handleInputChange('inquiryType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {inquiryTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => handleInputChange('subject', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Brief subject of your message"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message *
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your message..."
                      required
                    />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    {sending ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};